import { ExternalLink, Gift, Shield, TrendingUp, Users, CheckCircle } from 'lucide-react'
import { useAppLanguage } from '../i18n'

export default function AffiliateProgramLegalPage() {
  const { t } = useAppLanguage()

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Background layers */}
      <div
        aria-hidden
        className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900"
      />
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]"
      />
      <div
        aria-hidden
        className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_15%_18%,rgba(236,72,153,0.14),transparent_60%),radial-gradient(circle_at_85%_18%,rgba(168,85,247,0.17),transparent_55%)] dark:bg-[radial-gradient(circle_at_12%_18%,rgba(236,72,153,0.12),transparent_55%),radial-gradient(circle_at_88%_20%,rgba(168,85,247,0.15),transparent_50%)]"
      />

      <div className="relative mx-auto max-w-[1600px] px-6 py-12">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="mb-6 inline-flex items-center justify-center rounded-full bg-emerald-500/15 p-4 text-emerald-600 dark:text-emerald-400">
            <Gift className="h-8 w-8" />
          </div>
          <h1 className="text-4xl font-bold text-zinc-900 dark:text-white">
            Programme Partenaires Totemium
          </h1>
          <p className="mt-4 text-lg text-zinc-600 dark:text-zinc-400">
            Gagnez des commissions en recommandant Totemium à votre réseau
          </p>
        </div>

        {/* Cards principales */}
        <div className="mx-auto mb-12 grid max-w-5xl gap-6 md:grid-cols-3">
          <div className="rounded-3xl border border-emerald-100/70 bg-white/60 p-6 dark:border-emerald-900/40 dark:bg-zinc-900/40">
            <div className="mb-4 inline-flex items-center justify-center rounded-2xl bg-emerald-500/15 p-3 text-emerald-600 dark:text-emerald-400">
              <TrendingUp className="h-6 w-6" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-zinc-900 dark:text-white">
              30% de commission récurrente
            </h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              Recevez 30% de commission à chaque renouvellement d'abonnement payant de vos filleuls (mensuel ou annuel). <strong>Tant que votre filleul reste abonné, vous continuez de percevoir 30% chaque mois ou chaque année.</strong>
            </p>
          </div>

          <div className="rounded-3xl border border-emerald-100/70 bg-white/60 p-6 dark:border-emerald-900/40 dark:bg-zinc-900/40">
            <div className="mb-4 inline-flex items-center justify-center rounded-2xl bg-purple-500/15 p-3 text-purple-600 dark:text-purple-400">
              <Users className="h-6 w-6" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-zinc-900 dark:text-white">
              Suivi transparent
            </h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              Dashboard complet : statistiques, filleuls, commissions, vues de lien. Identifiants techniques uniquement (RGPD).
            </p>
          </div>

          <div className="rounded-3xl border border-emerald-100/70 bg-white/60 p-6 dark:border-emerald-900/40 dark:bg-zinc-900/40">
            <div className="mb-4 inline-flex items-center justify-center rounded-2xl bg-pink-500/15 p-3 text-pink-600 dark:text-pink-400">
              <Shield className="h-6 w-6" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-zinc-900 dark:text-white">
              Règles claires
            </h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              Conditions transparentes, interdiction d'auto-parrainage et de spam. Versements tracés et documentés.
            </p>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="mx-auto max-w-4xl space-y-8">
          {/* Comment ça marche */}
          <section className="rounded-3xl border border-emerald-100/70 bg-white/60 p-8 dark:border-emerald-900/40 dark:bg-zinc-900/40">
            <h2 className="mb-6 text-2xl font-bold text-zinc-900 dark:text-white">
              Comment ça marche ?
            </h2>
            
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-lg font-bold text-emerald-600 dark:text-emerald-400">
                  1
                </div>
                <div>
                  <h3 className="mb-2 font-semibold text-zinc-900 dark:text-white">
                    Partagez votre lien ou code
                  </h3>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400">
                    Chaque partenaire dispose d'un lien unique <code className="rounded bg-zinc-100 px-2 py-0.5 text-xs dark:bg-zinc-800">?ref=&lt;code&gt;</code> et d'un code de parrainage. Partagez-le sur vos réseaux, votre blog, ou directement à vos contacts.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-lg font-bold text-emerald-600 dark:text-emerald-400">
                  2
                </div>
                <div>
                  <h3 className="mb-2 font-semibold text-zinc-900 dark:text-white">
                    Vos filleuls s'inscrivent
                  </h3>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400">
                    Le système détecte automatiquement le parrainage via le cookie <code className="rounded bg-zinc-100 px-2 py-0.5 text-xs dark:bg-zinc-800">totemium_ref</code> (durée : 90 jours par défaut) ou via le code saisi à l'inscription.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-lg font-bold text-emerald-600 dark:text-emerald-400">
                  3
                </div>
                <div>
                  <h3 className="mb-2 font-semibold text-zinc-900 dark:text-white">
                    Recevez vos commissions
                  </h3>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400">
                    Lorsque vos filleuls souscrivent à un plan payant éligible, vous recevez une commission. Les versements sont gérés manuellement par l'équipe ou automatiquement via Stripe Connect (selon configuration).
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Règles et interdictions */}
          <section className="rounded-3xl border border-amber-100/70 bg-amber-50/40 p-8 dark:border-amber-900/40 dark:bg-amber-950/20">
            <h2 className="mb-6 text-2xl font-bold text-zinc-900 dark:text-white">
              Règles et bonnes pratiques
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                <p className="text-sm text-zinc-700 dark:text-zinc-300">
                  <strong>Transparence :</strong> Vous devez informer clairement votre public que vous utilisez un lien d'affiliation (conformément aux lois en vigueur).
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                <p className="text-sm text-zinc-700 dark:text-zinc-300">
                  <strong>Contenu honnête :</strong> Présentez Totemium de manière authentique. La publicité trompeuse est interdite.
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                <p className="text-sm text-zinc-700 dark:text-zinc-300">
                  <strong>Pas de spam :</strong> N'envoyez pas de messages non sollicités (email, réseaux sociaux, forums).
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                <p className="text-sm text-zinc-700 dark:text-zinc-300">
                  <strong>Auto-parrainage interdit :</strong> Vous ne pouvez pas utiliser votre propre lien pour bénéficier d'avantages.
                </p>
              </div>
            </div>
          </section>

          {/* Confidentialité */}
          <section className="rounded-3xl border border-emerald-100/70 bg-white/60 p-8 dark:border-emerald-900/40 dark:bg-zinc-900/40">
            <h2 className="mb-6 text-2xl font-bold text-zinc-900 dark:text-white">
              Protection des données (RGPD)
            </h2>
            
            <p className="mb-4 text-sm text-zinc-600 dark:text-zinc-400">
              Pour respecter la confidentialité de vos filleuls, votre dashboard d'affiliation affiche uniquement :
            </p>
            
            <ul className="space-y-2 text-sm text-zinc-700 dark:text-zinc-300">
              <li className="flex items-start gap-2">
                <span className="text-emerald-600 dark:text-emerald-400">•</span>
                <span>Un <strong>identifiant technique interne</strong> (ex: <code className="rounded bg-zinc-100 px-2 py-0.5 text-xs dark:bg-zinc-800">user_id: 12345</code>) par filleul</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-600 dark:text-emerald-400">•</span>
                <span>Les dates d'attribution et de commission</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-600 dark:text-emerald-400">•</span>
                <span>Les montants de commissions et leurs statuts</span>
              </li>
            </ul>
            
            <p className="mt-4 text-sm text-zinc-600 dark:text-zinc-400">
              <strong>Aucune donnée nominative</strong> (nom, prénom, email) n'est accessible dans votre espace affilié.
            </p>
          </section>

          {/* Paramètres et versements */}
          <section className="rounded-3xl border border-emerald-100/70 bg-white/60 p-8 dark:border-emerald-900/40 dark:bg-zinc-900/40">
            <h2 className="mb-6 text-2xl font-bold text-zinc-900 dark:text-white">
              Commissions et versements
            </h2>
            
            <div className="space-y-4 text-sm text-zinc-700 dark:text-zinc-300">
              <p>
                <strong>Taux de commission :</strong> 30% du montant de l'abonnement payant de votre filleul. Ce taux s'applique à chaque renouvellement (mensuel ou annuel).
              </p>
              
              <p>
                <strong>Récurrence :</strong> Vous percevez 30% à chaque paiement récurrent de votre filleul. <strong>Tant que votre filleul reste abonné à Totemium, vous continuez de recevoir 30% chaque mois (abonnement mensuel) ou chaque année (abonnement annuel)</strong>. Il n'y a pas de limite dans le temps.
              </p>
              
              <p>
                <strong>Plans éligibles :</strong> Seuls les abonnements payants éligibles génèrent des commissions. Le plan gratuit ne donne pas lieu à commission.
              </p>
              
              <p>
                <strong>Délai de versement :</strong> Un délai de sécurité (configurable) peut s'appliquer avant qu'une commission ne devienne payable (pour gérer les remboursements éventuels).
              </p>
              
              <p>
                <strong>Mode de versement :</strong> Les versements sont gérés via Stripe Connect (versements automatiques) ou manuellement par l'équipe Totemium selon la configuration de votre compte.
              </p>
            </div>
          </section>

          {/* Contact et documentation */}
          <section className="rounded-3xl border border-zinc-200/80 bg-zinc-50/40 p-8 dark:border-zinc-700/60 dark:bg-zinc-800/40">
            <h2 className="mb-4 text-xl font-bold text-zinc-900 dark:text-white">
              Besoin d'aide ?
            </h2>
            
            <p className="mb-4 text-sm text-zinc-600 dark:text-zinc-400">
              Pour toute question sur le programme partenaires, contactez votre administrateur ou l'équipe Totemium.
            </p>
            
            <div className="flex flex-wrap gap-3">
              <a
                href="/account/affiliate"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"
              >
                <Gift className="h-4 w-4" />
                Mon espace affilié
              </a>
              
              <a
                href="/contact"
                className="inline-flex items-center gap-2 rounded-full border border-emerald-200 px-5 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-950/50"
              >
                <ExternalLink className="h-4 w-4" />
                Nous contacter
              </a>
            </div>
          </section>

          {/* Note juridique */}
          <div className="rounded-3xl border border-zinc-200/80 bg-zinc-50/40 p-6 dark:border-zinc-700/60 dark:bg-zinc-800/40">
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              <strong>Note :</strong> Ce résumé est fourni à titre informatif. Les conditions complètes du programme partenaires sont définies dans le contrat d'affiliation que vous devez accepter dans votre espace affilié. En cas de divergence entre ce résumé et le contrat, le contrat prévaut.
            </p>
            <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
              Dernière mise à jour : Avril 2026 · Totemium SaaS
            </p>
          </div>
        </div>

        {/* Footer simple */}
        <div className="mt-16 border-t border-zinc-200/80 pt-8 text-center dark:border-zinc-700/60">
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            <a href="/" className="text-emerald-600 hover:underline dark:text-emerald-400">
              Retour à l'accueil
            </a>
            {' · '}
            <a href="/account" className="text-emerald-600 hover:underline dark:text-emerald-400">
              Mon compte
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
